/*
This computer program, as defined in the Copyright, Designs and Patents Act 1998 and the Software Directive (2009/24/EC), 
is the copyright of Logic Valley Ltd, a wholly owned subsidiary of Marston (Holdings) Ltd. All rights are reserved.
*/

/*
  In this files we configured router and menubar details
  #MenuLayout     : In this menulayout we mapped all our 
  #BrowserRouter  : BrowserRouter is a router implementation that uses the HTML5 history API (pushstate, replacestate, and popstate events) to keep your UI in sync with the URL.
                    It is the parent component used to store all other components.
  #RecoilRoot     : Recoil used to manage the global state value(s)
*/
// System defined variables
import React, { useState } from 'react'
import { ConfirmProvider } from 'material-ui-confirm'
import { useLocation } from 'react-router-dom'
import {
  AuthenticatedTemplate,
  MsalAuthenticationTemplate,
  useMsal,
} from '@azure/msal-react'
import { InteractionType } from '@azure/msal-browser'
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil'
import jwtDecode from 'jwt-decode'

// Custom defined variables
import { loginRequest } from '../configs/msalAuthConfigs'
import RoutesFile from '../views/routes/Routes'
import BackdropSpinner from '../views/components/base/backdropSpinner/BackdropSpinner'
import Toast from '../views/components/base/toast/Toast'
import MenuLayout from '../views/components/custom/menuLayout/MenuLayout'
import ErrorBoundary from '../views/components/custom/errorBoundary/ErrorBoundary'
// Loading component is used to pass as loading component for msalAuthentication
import TitleBar from '../views/pages/titleBar/TitleBar'
import {
  showSplashScreen,
  userTokenDetails,
  toastMessage,
  mappedUserRoles,
  assignedContractRoles,
  assignedDistinctRoles,
  userDetail,
  runtimeTokenExpiredAtom,
  errorCodes,
  gotInitialSessionResponseOfAuthenticationAtom,
  axiosConfiguration,
  selectedMenuData,
  airaLayoutChanges,
  hideTitleBarAtom,
} from '../views/components/recoil/atoms/atoms'
import UserPermission from '../views/pages/userPermission/UserPermission'
import apiEndPoints from '../../src/models/api/apiEndpoints'
import { fetchDataSelector } from '../views/components/recoil/selectors/selectors'
import utils from '../../src/utils/utils'
import { authenticationData } from '../views/components/recoil/atoms/atomsWithLocalStorage'
import IdleStateDetector from '../views/components/custom/idleStateDetector/IdleStateDetector'
import ResponseErrorCode from '../views/pages/responseErrorCode/ResponseErrorCode'
import './App.css'
import 'react-web-white-label/dist/app/global.css'

function App({ msalInstance, loginHintUserName }) {
  const setAuthenticationResult = useSetRecoilState(authenticationData)
  const setMappedUserRoles = useSetRecoilState(mappedUserRoles)
  const setAssignedContractRoles = useSetRecoilState(assignedContractRoles)
  const setAssignedDistinctRoles = useSetRecoilState(assignedDistinctRoles)
  const [loggedUserDetail, setLoggedInUserDetail] = useRecoilState(userDetail)

  const authenticatedUserData = useRecoilValue(authenticationData)
  const getCoreData = useRecoilValue(fetchDataSelector)
  const getSelectedMenuData = useRecoilValue(selectedMenuData)
  const [runtimeTokenExpired, setRuntimeTokenExpired] = useRecoilState(
    runtimeTokenExpiredAtom
  )
  const gotInitialSessionResponseOfAuthentication = useRecoilValue(
    gotInitialSessionResponseOfAuthenticationAtom
  )
  const [userPermissionCheckCall, setUserPermissionCheckCall] = useState(false)
  const [permittedUser, setPermittedUser] = useState(null)
  const [accountPermissionHint, setAccountPermissionHint] = useState(
    'Insufficient Permissions'
  )
  const showToastMessage = useSetRecoilState(toastMessage)
  // For authenticated user Splash screen will be shown once
  const setShowSplashScreen = useSetRecoilState(showSplashScreen)
  const setUserTokenDetails = useSetRecoilState(userTokenDetails)
  const responseErrorCodes = useRecoilValue(errorCodes)
  const [refreshLoginApi, setRefreshLoginApi] = useState(false)
  const configVariable = useRecoilValue(axiosConfiguration)
  const isAiraLayoutChanges = useRecoilValue(airaLayoutChanges)
  const hideTitleBar = useRecoilValue(hideTitleBarAtom)
  // Commenting below, due to instead of isAuthenticated from msal using local isAuthenticatedSilentFlag to check whether user has logged in or not
  // const isAuthenticated = useIsAuthenticated()
  const { instance } = useMsal()
  const location = useLocation()

  React.useEffect(() => {
    // added event listsner function to set show splash screen to false before refresh and the same will persis and splash screen will not be shown after page refresh
    window?.addEventListener('beforeunload', () => {
      setShowSplashScreen(false)
    })
  }, [])
  const availableRoleValidation = (roleData) => {
    if (typeof roleData === 'object') {
      roleData?.forEach((roleName) => {
        if (!utils?.userRoleCheck?.includes(roleName)) {
          return false
        }
      })
    } else {
      if (!utils?.userRoleCheck?.includes(roleData)) {
        return false
      }
    }
    return true
  }

  const getAiraManagerLoginTime = async () => {
    const postData = {
      isLoggedIn: true,
    }

    getCoreData(
      apiEndPoints?.postAIRAManagerloginTime?.method,
      `${apiEndPoints?.postAIRAManagerloginTime?.url}`,
      postData
    )
      .then((response) => {
        if (response?.status === utils?.apiResponseCode?.successCode) {
          // console.log('Response:', response)
        }
      })
      .catch(() => {
        // console.log('Error in update login API')
      })
  }
  React.useEffect(() => {
    if (!loggedUserDetail || !authenticatedUserData) return
    // Skip if token is expired — will re-run after token refresh updates authenticatedUserData
    const exp = authenticatedUserData?.idTokenClaims?.exp
    if (exp && exp * 1000 < Date.now()) return
    // As per Bug 136801: Invalid Data displayed in the Usage Report - The last login details required for all roles and users. So we removed the role validation

    getAiraManagerLoginTime()
  }, [loggedUserDetail, authenticatedUserData])
  async function fetchUserTokenDetails() {
    Promise.all([
      await getCoreData(
        apiEndPoints?.userPermissionCheck?.method,
        `${apiEndPoints?.userPermissionCheck?.url}`,
        '',
        { Authorization: 'Bearer' + ' ' + authenticatedUserData?.idToken },
        null,
        true,
        'usePermission'
      ),
    ]).then(([response]) => {
      if (response?.status === utils?.apiResponseCode?.successCode) {
        var token = response?.data?.token
        var decoded = jwtDecode(token)
        setLoggedInUserDetail(decoded)
        // will get distinct roles and roles for assigned each contract from this decode
        if (availableRoleValidation(decoded?.role)) {
          // initially set distinct roles to mappedUserRole. Will change this based on contract once clicked any contract widget.
          if (typeof decoded?.role === 'string') {
            setMappedUserRoles([decoded?.role])
            setAssignedDistinctRoles([decoded?.role])
          } else {
            setMappedUserRoles(decoded?.role)
            setAssignedDistinctRoles(decoded?.role)
          }
          // will have the JSON of assigned contracts with contractId,name and role
          setAssignedContractRoles(decoded?.contract)

          setUserTokenDetails(response?.data)
          setPermittedUser(true)
        } else {
          setAccountPermissionHint('Insufficient Permissions')
          setPermittedUser(false)
        }
      } else if (response?.data?.validationErrors?.length > 0) {
        if (
          response?.data?.validationErrors[0]?.errorMessage
            .toLocaleLowerCase()
            .includes('insufficient')
        ) {
          setAccountPermissionHint(
            utils?.userPermissionMessage?.insufficientPermission
          )
        } else if (
          response?.data?.validationErrors[0]?.errorMessage
            .toLocaleLowerCase()
            .includes('account')
        ) {
          setAccountPermissionHint(
            utils?.userPermissionMessage?.accountInactive
          )
        } else {
          setAccountPermissionHint(
            response?.data?.validationErrors[0]?.errorMessage
          )
        }
        setPermittedUser(false)
      } else if (response?.data) {
        setPermittedUser(false)
        showToastMessage({
          message: response.data?.errors,
          status: 'error',
        })
      }
      //setPermittedUser(true)
      setUserPermissionCheckCall(true)
      setRefreshLoginApi(false)
    })
  }
  //User permission check and validation
  React.useEffect(() => {
    if (
      authenticatedUserData &&
      gotInitialSessionResponseOfAuthentication &&
      !userPermissionCheckCall &&
      !runtimeTokenExpired
    ) {
      fetchUserTokenDetails()
    } else if (runtimeTokenExpired) {
      setUserPermissionCheckCall(true)
    }
  }, [
    authenticatedUserData,
    gotInitialSessionResponseOfAuthentication,
    runtimeTokenExpired,
  ])
  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (refreshLoginApi) {
      fetchUserTokenDetails()
    }
  }, [refreshLoginApi])

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  const getNewTokenForLoggedInUser = () => {
    const accessTokenRequest = {
      scopes: ['openid'],
      account: authenticatedUserData?.account,
      forceRefresh: true,
      tokenQueryParameters: { ...authenticatedUserData },
    }

    instance
      .acquireTokenSilent(accessTokenRequest)
      .then((response) => {
        setAuthenticationResult(response)
        setRefreshLoginApi(true)
      })
      .catch(() => {})
  }

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  const getWhenToRefreshTokenInMillSeconds = () => {
    const expiresOn = new Date(authenticatedUserData?.idTokenClaims?.exp * 1000) //authenticatedUserData?.idTokenClaims?.exp - this will have timeStamp. So multiple * 1000
    const now = new Date(utils?.returnCurrentDateTimeInUTC())

    const diffInMinutes = (expiresOn - now) / 1000 / 60

    let executeAfter = 0
    // if (diffInMinutes >= utils?.refreshTokenMinutesEarlier) {
    executeAfter = (diffInMinutes - utils?.refreshTokenMinutesEarlier) * 60000
    // }
    return executeAfter
  }

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (authenticatedUserData) {
      const executeAfter = getWhenToRefreshTokenInMillSeconds()
      if (executeAfter <= 0) {
        // Token already expired or about to — refresh immediately before any API call
        getNewTokenForLoggedInUser()
      } else {
        setTimeout(() => {
          getNewTokenForLoggedInUser()
        }, [executeAfter])
      }
    }
  }, [authenticatedUserData])

  // ignoring this due to refresh time will get from token
  /* istanbul ignore next */
  React.useEffect(() => {
    if (runtimeTokenExpired) {
      setAccountPermissionHint(utils?.tokenExpiredMessage)
    }
  }, [runtimeTokenExpired])

  const bodyStyles = document.body.style

  bodyStyles.setProperty('--carddatatitlefontSize', '19px')
  bodyStyles.setProperty('--header-FontSize', '28px')
  bodyStyles.setProperty('--sub-header-FontSize', '24px')
  bodyStyles.setProperty('--label-FontSize', '16px')
  bodyStyles.setProperty('--heading-FontSize', '32px')
  bodyStyles.setProperty('--subHeading-FontSize', '21px')
  bodyStyles.setProperty('--caseSubHeading-FontSize', '18px')
  bodyStyles.setProperty('--mainheading-FontSize', '24px')
  bodyStyles.setProperty('--listHeader-FontSize', '12px')
  bodyStyles.setProperty('--selectedSubmenu-fontColor', '#333333')
  bodyStyles.setProperty('--inputadornmenttextheight', '13px')
  bodyStyles.setProperty('--additionalTabSideHead', '18px')
  bodyStyles.setProperty('--pcnHeaderFontSize', '18px')
  bodyStyles.setProperty('--headerFontSize', '18px')
  //button--start
  bodyStyles.setProperty('--buttonIconSize', '12px')
  bodyStyles.setProperty('--buttonFontSize', '15px')
  bodyStyles.setProperty('--buttonFontWeight', '500')
  bodyStyles.setProperty('--buttonIconTextGap', '15px')
  bodyStyles.setProperty('--buttonTopBottomPadding', '11px')
  bodyStyles.setProperty('--buttonRightLeftPadding', '22px')
  bodyStyles.setProperty('--buttonBorderRadius', '4px')
  bodyStyles.setProperty('--userCountBand-bg-color', '#E9F2FF')
  bodyStyles.setProperty('--hyperlinkFont-color', 'blue')
  //button-end
  //fieldAlert start
  bodyStyles.setProperty('--fieldAlertFontSize', '14px')
  //fieldAlert end
  //inputControl - start
  bodyStyles.setProperty('--inputControlTextPadding', '10px')
  bodyStyles.setProperty('--inputCalenderIconSize', '24px')
  bodyStyles.setProperty('--inputDropDownIconSize', '16px')
  bodyStyles.setProperty('--placeHolderColor', '#B6B6B6')
  bodyStyles.setProperty('--inputControlBorderColor', '#B6B6B6')
  bodyStyles.setProperty('--inputControlBorderSize', '1px')
  // need to follow entire base controls
  bodyStyles.setProperty('--inputControlDisabledTextColor', '#898989')
  bodyStyles.setProperty('--inputControlDisabledBgColor', '#F3F3F3')
  bodyStyles.setProperty('--inputbackgroundColor', '#FBFBFB')

  //inputControl - end
  //Dialogue - start
  bodyStyles.setProperty('--dialogTitleFontSize', '16px')
  bodyStyles.setProperty('--dialogTitleFontWeight', '700')
  bodyStyles.setProperty('--dialogContentFontSize', '16px')
  bodyStyles.setProperty('--dialogContentFontWeight', '400')
  bodyStyles.setProperty('--dialogBorderRadius', '10px')
  bodyStyles.setProperty('--dialogIconFontSize', '16px')
  //Dialogue - end
  //toast - start
  bodyStyles.setProperty('--errorToastColor', '#ef5350')
  bodyStyles.setProperty('--successToastColor', '#1F3C88')
  //toast - end
  //switch - start
  bodyStyles.setProperty('--unStyledSwitchColor', '#1F3C88')
  bodyStyles.setProperty('--unStyledSwitchDisabledColor', '#1F3C88')
  //switch - end
  //list - start
  bodyStyles.setProperty('--listHeaderFontWeight', '600')
  bodyStyles.setProperty('--listHeaderFontColor', '#000000')
  bodyStyles.setProperty('--listHeaderFontSize', '16px')
  //list - end
  //contractCard - start
  bodyStyles.setProperty('--contractCardTitleFontSize', '24px')
  bodyStyles.setProperty('--contractCardSubHeaderFontWeight', '350')
  bodyStyles.setProperty('--contractCardSubHeaderFontColor', '#9D9D9D')
  bodyStyles.setProperty('--contractCardTitleFontColor', '#3A3A3A')
  bodyStyles.setProperty('--contractCardCountTitleFontColor', '#808080')
  bodyStyles.setProperty('--contractCardCountTitleFontSize', '19px')
  bodyStyles.setProperty('--contractCardCountFontSize', '24px')
  bodyStyles.setProperty('--contratcCardAvatarSize', '72px')
  bodyStyles.setProperty('--contractCardLogoHeight', '51.55px')
  bodyStyles.setProperty('--contractCardLogoWidth', '36px')
  //contractCard - end
  //caseOverView - start
  bodyStyles.setProperty('--gridTitleFontSize', '20px')
  bodyStyles.setProperty('--gridTitleFontColor', '#535353')
  bodyStyles.setProperty('--gridTitleFontWeight', '600')
  bodyStyles.setProperty('--countTitleFontColor', '#808080')
  bodyStyles.setProperty('--countValueFontColor', '#333333')
  bodyStyles.setProperty('--TaskOverviewBorderColor', '#BCBDBE')
  bodyStyles.setProperty('--countValueFontSize', '32px')
  //caseOverview - end
  //tooltip - start
  bodyStyles.setProperty('--tooltipBackgroundColor', '#838383') //got this color from show&tell #e1dbdb //this color from figma - 838383
  bodyStyles.setProperty('--tooltipFontColor', '#FFFFFF') // changing from #FFFFFF to #000000 for visibility
  bodyStyles.setProperty('--tooltipFontSize', '14px')
  bodyStyles.setProperty('--tooltipBorderRadius', '4px')

  //tooltip - end
  // muiIcon size - start
  bodyStyles.setProperty('--muiIconSize', '12px')
  // muiIcon size - end
  // AccordionTitle - start
  bodyStyles.setProperty('--accordionTitleFontSize', '12px')
  // AccordionTitle - end
  // ActionButton - start
  bodyStyles.setProperty('--ActionButtonTitleFontSize', '15px')
  bodyStyles.setProperty('--ActionButtonIconFontSize', '22px')
  bodyStyles.setProperty('--ActionButtonDescriptionFontSize', '14px')
  // ActionButton - end
  // ActionConfirmation - start
  bodyStyles.setProperty('--ActionConfirmationDataFontSize', '14px')
  bodyStyles.setProperty('--ActionCancellationReasonLabelFontColor', '#5A5A5A')
  // ActionConfirmation - end
  // button - start
  bodyStyles.setProperty('--buttonHeight', '40px')
  // button - end
  //mediaViewer - start
  bodyStyles.setProperty('--attachmentViewerBgColor', '#d3d3d3')
  //mediaViewer - end
  /* */

  bodyStyles.setProperty('--accordionDivHeight', '0px')
  bodyStyles.setProperty('--default-FontName', 'Segoe UI')
  // bodyStyles.setProperty('--default-FontName', `'Open Sans',sans-serif`)
  bodyStyles.setProperty('--default-FontColour', 'black')
  bodyStyles.setProperty('--secondary-FontColour', '#1F3C88')
  bodyStyles.setProperty('--default-FontWeight', '400')
  bodyStyles.setProperty('--default-FontSize', '16px')
  bodyStyles.setProperty('--default-BellbuttonFontSize', '10px')
  bodyStyles.setProperty('--default-BellbuttonHeight', '12px')
  bodyStyles.setProperty('--default-BellbuttonWidth', '12px')
  bodyStyles.setProperty('--default-NotificationContentHeadingFontSize', '18px')
  bodyStyles.setProperty('--default-NotificationDateFontSize', '14px')
  bodyStyles.setProperty(
    '--default-NotificationContentUnReadRowColor',
    '#eef3ff'
  )
  bodyStyles.setProperty(
    '--default-NotificationContentRowBorderColor',
    '#e9e9e9'
  )
  bodyStyles.setProperty('--default-NotificationContentReadColor', '#333333')
  bodyStyles.setProperty('--default-NotificationContentUnReadColor', '#000000')
  bodyStyles.setProperty('--label-FontName', 'Segoe UI')
  bodyStyles.setProperty('--label-FontColour', 'Black')
  bodyStyles.setProperty('--label-FontWeight', '400')
  bodyStyles.setProperty('--button-color', '#1F3C88')
  bodyStyles.setProperty('--button-secondary-color', '#ffffff')
  bodyStyles.setProperty('--button-disabled-color', '#B6B6B6')
  bodyStyles.setProperty('--on-hold-status-color', '#ff6c00')
  bodyStyles.setProperty('--templateHeaderSelectedFontColor', '#1F3C88')
  bodyStyles.setProperty('--button-secondary-border-color', '#1F3C88')
  bodyStyles.setProperty('--brandingPaymentURLBGColor', '#EDEDED')
  bodyStyles.setProperty('--brandingActiveMenuBackgroundColor', '#E6E6E6')
  bodyStyles.setProperty('--gridbgcolor', '#f5f5f5')
  bodyStyles.setProperty('--appcontent-bg-color', '#EEEEEE')
  bodyStyles.setProperty('--appcontent-bg-layer-color', '#F9F9F9')
  bodyStyles.setProperty('--font-letter-space', '0px')
  bodyStyles.setProperty('--text-line-height', '1.25vw')
  bodyStyles.setProperty('--textahrlignment', null)
  bodyStyles.setProperty('--footertextahrlignment', 'center')
  bodyStyles.setProperty('--cursorurl', null)
  bodyStyles.setProperty('--cursorname', null)
  bodyStyles.setProperty('--required-color', 'red')
  bodyStyles.setProperty('--ToolbarTextColour', 'Black')
  bodyStyles.setProperty('--footer-default-fontcolor', '#828282')
  bodyStyles.setProperty('--submenufontcolour', '#838383')
  bodyStyles.setProperty('--controlborder-colour', null)
  bodyStyles.setProperty('--header-FontWeight', '600')
  bodyStyles.setProperty('--heading-FontColour', 'Black')
  bodyStyles.setProperty('--subHeading-FontColour', '#0054a6')
  bodyStyles.setProperty('--listHeader-FontColour', '#798093')
  bodyStyles.setProperty('--ListHeaderText-Colour', 'black')
  bodyStyles.setProperty('--TitleBar-TextColour', 'White')
  bodyStyles.setProperty('--cardfontColour', '#808080')
  bodyStyles.setProperty('--ToolbarBackgroundColour', 'White')
  bodyStyles.setProperty('--navbar-color', '#F8F8FF')
  bodyStyles.setProperty('--footer-color', '#F3F3F6')
  bodyStyles.setProperty('--navbar-group-color', '#F3F3F3')
  bodyStyles.setProperty('--titlebar-color', '#F3F3F6')
  bodyStyles.setProperty('--TitleBar-IconColour', '#1F3C88')
  bodyStyles.setProperty('--sub-header-FontWeight', '600')
  bodyStyles.setProperty('--cardavatarbgcolor', '#F1F5FF')
  bodyStyles.setProperty('--carddatatitlebordercolor', '#ECECEC')
  bodyStyles.setProperty('--disableBgColor', '#D9D9D9')
  bodyStyles.setProperty('--disabledDropdownIconColor', '#7d7d7d')
  bodyStyles.setProperty('--disabledDropdownFieldColor', '#E6E6E6')
  bodyStyles.setProperty('--disabledDropdownTextColor', '#B6B6B6')
  bodyStyles.setProperty('--actionButtonBgColor', '#E9F2FF')
  bodyStyles.setProperty('--searchBarControlBorder-colour', '#B6B6B6')
  bodyStyles.setProperty('--attachmentTextColor', '#545454')
  bodyStyles.setProperty('--confirmationTextColor', '#5a5a5a')
  bodyStyles.setProperty('--paymentStatusSuccess', '#29821a')
  bodyStyles.setProperty('--paymentStatusFailure', '#da3131')
  bodyStyles.setProperty('--paymentStatusRemoved', '#8c8c8c')
  bodyStyles.setProperty('--copyIconMouseDownColor', '#ccc9c9')
  bodyStyles.setProperty('--noRecordFoundFontColor', '#b6b6b6')
  bodyStyles.setProperty('--mandatorySymbol', '#ff0000')
  bodyStyles.setProperty('--greenColour', '#278832')
  bodyStyles.setProperty('--whiteColour', '#ffffff')
  bodyStyles.setProperty('--blackColour', '#000000')
  bodyStyles.setProperty('--Freeflow-flight-grey', '#E6E6E6')
  bodyStyles.setProperty('--Freeflow-Grey', '#838383')
  bodyStyles.setProperty('--text-disabled', '#162B4861')
  bodyStyles.setProperty('--FF-Dark-Grey', '#646464')
  bodyStyles.setProperty('--inputControlBackgroundColor', '#ffffff')
  bodyStyles.setProperty('--control-Radius', '6px')
  bodyStyles.setProperty('--attachmentIcon-Border', '#B4C4EE')
  bodyStyles.setProperty('--line-BorderColor', '#ececec')
  bodyStyles.setProperty('--tab-White', '#eef3ff')
  bodyStyles.setProperty('--readOnlyColor', '#8F9DC3')
  bodyStyles.setProperty('--menuLayoutBoxShadow', '#888888')
  bodyStyles.setProperty('--disableMenuColor', '#bbb9b9')
  bodyStyles.setProperty('--tagsBorderColor', '#CAC5C5')
  bodyStyles.setProperty('--messageModalTitleBar', '#e3e3e3')
  bodyStyles.setProperty('--muicheckedHover', '#0000000a')
  bodyStyles.setProperty('--tagsBGColor', '#cfd8e2')
  bodyStyles.setProperty('--gridCellIconWidth', '24px')
  bodyStyles.setProperty('--gridFileErrorCount', '#7b818e')
  bodyStyles.setProperty('--containerBorderRadius', '8px')
  bodyStyles.setProperty('--remainingIndicateTextColor', '#ababab')
  bodyStyles.setProperty('--createdPcnFontSize', '26px')
  bodyStyles.setProperty('--emailControler', '#cfd8e2')
  bodyStyles.setProperty('--statusInitiated-background', '#e6edff')
  bodyStyles.setProperty('--statusCompleted-background', '#c4edc8')
  bodyStyles.setProperty('--statusCompleted-fontcolor', '#219360')
  bodyStyles.setProperty('--tagspopBgColor', '#999')
  bodyStyles.setProperty('--messagepopupscrollbarBgColor', '#aeaaaa57')
  bodyStyles.setProperty('--messagepopupscrollbarColor', '#ffffff57')
  bodyStyles.setProperty('--messagepopupBorderColor', '#e3e3e3')
  bodyStyles.setProperty('--popupBgColor', '#00000080')
  bodyStyles.setProperty('--disableSwitchTrackBgColor', '#1f3c8880')
  bodyStyles.setProperty('--scrollBarBgColor', '#aeaaaa57')
  bodyStyles.setProperty('--scrollBarBorderColor', '#ffffff57')
  bodyStyles.setProperty('--additionalTabBorderColor', '#bcbcbc')
  bodyStyles.setProperty('--inputLableColor', '#646464')
  bodyStyles.setProperty('--popupBackgroundColor', '#00000080')
  bodyStyles.setProperty('--fpnDisabledColor', '#8E9DC3')
  bodyStyles.setProperty('--popupLabelColor', '#606060')
  bodyStyles.setProperty('--popupStageColor', '#3eb54b')
  bodyStyles.setProperty('--inputBackground', '#e6e6e6')
  bodyStyles.setProperty('--attachmentFiletype-Heading', '350')
  bodyStyles.setProperty('--attachmentFiletype-Heading-Font-Size', '13px')
  bodyStyles.setProperty('--ceoTrackTitleColor', '#262D33')
  bodyStyles.setProperty('--priorityFieldBGColor', '#F3F5FC')
  bodyStyles.setProperty('--conditionBorderColor', '#97AEE7')
  bodyStyles.setProperty('--popupTItleColor', '#243B6A')
  bodyStyles.setProperty('--actionButtonBackground', '#F3F5FC')
  bodyStyles.setProperty('--grayBgColor', '#F2F2F2')
  bodyStyles.setProperty('--blueTextColor', '#0C7FE1')
  bodyStyles.setProperty('--mediumColor', '#FFC107')
  bodyStyles.setProperty('--lowColor', '#1A936F')
  bodyStyles.setProperty('--autocompleteAdornmentIconColor', '#162B488F')
  bodyStyles.setProperty('--hyperlinkTextColor', '#0068FF')
  bodyStyles.setProperty('--jobCancelledBackColor', '#FEEBEE')
  bodyStyles.setProperty('--JobAmountBackColor', '#fff3e0')
  bodyStyles.setProperty('--vehicleLogoBackgroundColor', '#D0DEE9')
  bodyStyles.setProperty('--vehicleLogoColor', '#89A9C2')
  bodyStyles.setProperty('--filterColumnBorder', '#80808038')
  bodyStyles.setProperty('--poundpaymentsuucess', '#2a7f62')
  bodyStyles.setProperty('--poundpaymentsuucessBgcolor', '#f1f8e9')
  bodyStyles.setProperty('--poundpaymentsuucessBgcolorData', '#lightGreen')
  bodyStyles.setProperty('--wfAutoAssignTextBgcolor', '#c7d7ff')
  bodyStyles.setProperty('--wfWidget-border-line', '#e0e0e0')
  bodyStyles.setProperty('--wf-overlay-bg', '#00000040')
  bodyStyles.setProperty('--wf-popup-box-shadow', '0 8px 32px #0000002E')
  bodyStyles.setProperty('--wf-utr-section-bg', '#F9F9F999')
  bodyStyles.setProperty('--widget-complete-color', '#4289ff')
  bodyStyles.setProperty('--widget-calender-date-color', '#0066cc')
  bodyStyles.setProperty('--widget-calender-range-date-color', '#e6f0ff')
  bodyStyles.setProperty('--purgeCaseBadgeBackground', '#FEEEEE')
  bodyStyles.setProperty('--airaBackColor', '#f4f9ff')
  bodyStyles.setProperty('--airaButtonDisableBackColor', '#162b4861')
  bodyStyles.setProperty('--airaButtonDisableColor', '#162b481f')
  bodyStyles.setProperty('--airaTextButtonHoverColor', '#1976D2')
  bodyStyles.setProperty('--dpscomponentBorderColor', '#162b483b')
  bodyStyles.setProperty('--airaRedColor', '#D32F2F')
  bodyStyles.setProperty('--airaBgColor', '#f4f9ff')
  bodyStyles.setProperty('--airaLightBlueColor', '#f0f6ff')
  bodyStyles.setProperty('--airaLabelFontSize', '14px')
  bodyStyles.setProperty('--airaPlaceholderFontSize', '16px')
  bodyStyles.setProperty('--airaSubReasonTextColor', '#344761')
  bodyStyles.setProperty('--airaWarningAlertColor', '#663c00')
  bodyStyles.setProperty('--airaFormBorderColor', '#9cc3ff')
  bodyStyles.setProperty('--airagridHeaderColor', '#252627cc')
  bodyStyles.setProperty('--airaParagraphFontColor', '#242526')
  bodyStyles.setProperty('--airaParagraphNumberColor', '#fff9c4')
  bodyStyles.setProperty('--airaTextColor', '#162B48')
  bodyStyles.setProperty('--airaFormLabelColor', '#162B4899')
  bodyStyles.setProperty('--airaFormPlaceholderColor', '#162B48DE')
  bodyStyles.setProperty('--airaTextLinkColor', '#1da5c8')
  bodyStyles.setProperty('--airahoveBGColor', '#e0ecf8')
  bodyStyles.setProperty('--emailAvailableBackColor', '#eafbea')
  bodyStyles.setProperty('--emailAvailableTextColor', '#2b7f63')
  bodyStyles.setProperty('--emailNotAvailableBackColor', '#ffebeb')
  bodyStyles.setProperty('--emailNotAvailableTextColor', '#d3454b')
  bodyStyles.setProperty('--muiGridFilterButtonBgColor', '#303f9f24')
  bodyStyles.setProperty('--muiGridFilterTextColor', '#303f9f')
  bodyStyles.setProperty('--dialogDescTextColor', '#252627')
  bodyStyles.setProperty('--airaTextBorderColor', '#162b483b')
  bodyStyles.setProperty('--airaSelectionHoverColor', '#bcc2cb1f')
  bodyStyles.setProperty('--airaInActiveBgCOlor', '#fafafa')
  bodyStyles.setProperty('--airaGridChipBgColor', '#FFE082')
  bodyStyles.setProperty('--airaButtonGroupBgColor', '#757575')
  bodyStyles.setProperty('--airaChipBgColor', '#00000014')
  bodyStyles.setProperty('--helperIconBgColor', '#0000008c')
  bodyStyles.setProperty('--docIconColor', '#0D6EFD')
  bodyStyles.setProperty('--warningColor', '#EF6C00')
  bodyStyles.setProperty('--attachmentTitleColor', '#041643')

  bodyStyles.setProperty('--moreVertIconColor', '#0000008a')
  bodyStyles.setProperty('--favIconFileNameColor', '#3276CF')
  bodyStyles.setProperty('--linkedcases-summaryfield', '#0057b8')
  bodyStyles.setProperty('--bluedark-holdtask', '#1F3C88')
  bodyStyles.setProperty('--awaiting-qa-indicator-color', '#FB8C00')
  bodyStyles.setProperty('--hyperlink-file-name', '#336afa')
  bodyStyles.setProperty('--vertical-line-color', '#acacac')
  bodyStyles.setProperty('--stepper-border-color', '#d0d0d0')
  bodyStyles.setProperty('--stepper-active-color', '#0468c8')
  bodyStyles.setProperty('--stepper-completed-color', '#008C76')
  bodyStyles.setProperty('--thumbnail-border-color', '#8699d1')
  bodyStyles.setProperty('--edit-icon-color', '#0078d4')
  bodyStyles.setProperty('--vrm-container-color', '#F9DF1A')
  bodyStyles.setProperty('--docs-black-color', '#222')
  bodyStyles.setProperty('--docs-viewer-header-color', '#1a1a1a')
  bodyStyles.setProperty('--docs-viewer-table-header-color', '#ccc')
  bodyStyles.setProperty('--docs-loading-content-color', '#666')
  bodyStyles.setProperty('--abandonColor', '#E53935')
  bodyStyles.setProperty('--priority-yes-color', '#1e7e34')
  bodyStyles.setProperty('--priority-yes-bg', '#e6f4ea')
  bodyStyles.setProperty('--priority-no-color', '#e65100')
  bodyStyles.setProperty('--priority-no-bg', '#fff3e0')
  // Enforcement Map - start
  bodyStyles.setProperty('--em-success-bg', '#e8f5e9')
  bodyStyles.setProperty('--em-success-color', '#2e7d32')
  bodyStyles.setProperty('--em-error-bg', '#feebee')
  bodyStyles.setProperty('--em-error-color', '#c62828')
  bodyStyles.setProperty('--em-info-bg', '#e3f2fd')
  bodyStyles.setProperty('--em-info-color', '#1565c0')
  bodyStyles.setProperty('--em-info-border', '#90caf9')
  bodyStyles.setProperty('--em-warning-bg', '#fff3e0')
  bodyStyles.setProperty('--em-warning-border', '#ffcc80')
  bodyStyles.setProperty('--em-warning-color', '#e65100')
  bodyStyles.setProperty('--em-neutral-bg', '#f5f5f5')
  bodyStyles.setProperty('--em-neutral-color', '#616161')
  bodyStyles.setProperty('--em-chip-bg', '#a2c1ea')
  bodyStyles.setProperty('--em-chip-color', '#275798')
  bodyStyles.setProperty('--em-obs-border', '#43a047')
  bodyStyles.setProperty('--em-pcn-border', '#e53935')
  bodyStyles.setProperty('--em-badge-medium', '#FF9800')
  bodyStyles.setProperty('--em-row-border', '#eeeeee')
  // Enforcement Map - end
  // Integration File Transfer - start
  bodyStyles.setProperty('--ift-card-border', '#eaeef3')
  bodyStyles.setProperty('--ift-primary-icon-bg', 'rgba(50, 118, 207, 0.1)')
  bodyStyles.setProperty('--ift-success-icon-bg', 'rgba(34, 160, 107, 0.12)')
  bodyStyles.setProperty('--ift-success-color', '#1f8a5c')
  bodyStyles.setProperty('--ift-warning-icon-bg', 'rgba(242, 169, 59, 0.15)')
  bodyStyles.setProperty('--ift-warning-color', '#b97a12')
  bodyStyles.setProperty('--ift-info-icon-bg', 'rgba(50, 118, 207, 0.12)')
  bodyStyles.setProperty('--ift-danger-icon-bg', 'rgba(225, 75, 75, 0.12)')
  bodyStyles.setProperty('--ift-danger-color', '#c22a2a')
  bodyStyles.setProperty('--ift-label-color', '#5b6573')
  bodyStyles.setProperty('--ift-value-color', '#212121')
  // Integration File Transfer - end
  bodyStyles.setProperty('--fault-status-open-color', '#2196f3')
  bodyStyles.setProperty('--fault-status-inprogress-color', '#ff9800')
  bodyStyles.setProperty('--fault-status-closed-color', '#4caf50')
  bodyStyles.setProperty('--fault-status-rejected-color', '#f44336')
  bodyStyles.setProperty('--fault-status-default-color', '#9e9e9e')

  const ApplicationInsightsModuleLogValue =
    configVariable?.VariableSettings?.ApplicationInsightsModuleLogValue
  localStorage.setItem(
    'ApplicationInsightsModuleLogValue',
    ApplicationInsightsModuleLogValue
  )

  bodyStyles.setProperty('--sub-heading-text-color', '#5a5a5a')

  const AppInsightsKey = configVariable?.VariableSettings?.AppInsightsKey
  localStorage.setItem('AppInsightsKey', AppInsightsKey)

  return (
    <ErrorBoundary>
      <ConfirmProvider>
        <BackdropSpinner />
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={{
            ...loginRequest,
            loginHint: loginHintUserName,
          }}
        >
          <AuthenticatedTemplate>
            <>
              {responseErrorCodes &&
              responseErrorCodes?.showErrorPage === true ? (
                <ResponseErrorCode
                  responseErrorCode={responseErrorCodes?.errorCode}
                />
              ) : !userPermissionCheckCall ? (
                <></>
              ) : permittedUser && !runtimeTokenExpired ? (
                <>
                  <Toast />

                  <div
                    className={`boApplication ${
                      utils?.hideTitleBarForPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      ) || hideTitleBar
                        ? 'hide-title-bar'
                        : ''
                    } ${
                      utils?.reportSubMenuPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      )
                        ? 'report-submenu-layout'
                        : ''
                    } ${
                      utils?.airaPagesPaths?.includes(
                        location?.pathname?.toString()?.toLowerCase()
                      )
                        ? 'aira-layout'
                        : ''
                    }`}
                  >
                    <TitleBar msalInstance={msalInstance} />
                    <MenuLayout />
                    <div
                      className={`bodypage ${
                        getSelectedMenuData?.projectType
                          ?.toString()
                          ?.toLowerCase() ===
                          utils?.airaNames?.airaName
                            ?.toString()
                            ?.toLowerCase() || isAiraLayoutChanges
                          ? utils?.airaNames?.nonNPS?.toString()?.toLowerCase()
                          : ''
                      }`}
                    >
                      <div
                        className={`body-data-page ${
                          getSelectedMenuData?.projectType
                            ?.toString()
                            ?.toLowerCase() ===
                            utils?.airaNames?.airaName
                              ?.toString()
                              ?.toLowerCase() || isAiraLayoutChanges
                            ? utils?.airaNames?.nonNPS
                                ?.toString()
                                ?.toLowerCase()
                            : ''
                        }`}
                      >
                        <RoutesFile />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                (permittedUser !== null || runtimeTokenExpired) && (
                  <UserPermission
                    accountPermissionHint={accountPermissionHint}
                    msalInstance={msalInstance}
                  />
                )
              )}
            </>
          </AuthenticatedTemplate>
        </MsalAuthenticationTemplate>
      </ConfirmProvider>
    </ErrorBoundary>
  )
}

export default App
